// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import loadable from '@loadable/component';
import { clientLogger } from '@seedplanner/logger';
import { TASK_STATUS, sendToServer, utils } from '@seedplanner/shared-utils';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useGating, useUserContext } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import { CheckCircleIcon, DownloadIcon, PictureAsPdfIcon } from '../../lib/icons';
import { DASHBOARD_TASKS_COMPLETED_TODAY_QUERY } from '../../queries/DashboardQueries';
import { EDIT_TASK_MUTATION } from '../../queries/EditTaskQueries';
import * as routes from '../../routes';
import type {
  ChangeTaskGroupStatusMutation,
  ChangeTaskGroupStatusMutationVariables,
} from '../../types/__generated__/graphql';
import { downloadCSV, getNumericID, getTimeRanges } from '../../utils';
import { Options } from '../common/dropdown';
import LoadingMenuItem from './LoadingMenuItem';
import type { TaskData } from './TaskGroupTable';
import { groupTasksWithStatus } from './groupTasks';
import { createCSVContentFromTaskGroups } from './makeTaskCSV';
import { generateTodoPDF, taskGroupsToStringList } from './makeTaskPDF';

interface Props {
  readonly tasks: readonly TaskData[];
  readonly onSuccess?: () => void;
  readonly downloadTitle: string;
  readonly showComplete?: boolean;
  readonly useArrowOnly?: boolean;
}

// This prefetches when the component is mounted
const LoadableJSPDF = loadable.lib(() => import(/* webpackPrefetch: true */ 'jspdf'));

export default function TaskTableOptions({ tasks, onSuccess, downloadTitle, showComplete, useArrowOnly }: Props) {
  const { permissions, timezone } = useUserContext();
  const { canUseBasic } = useGating();
  const { todayStart, todayEnd } = getTimeRanges(timezone);
  const userCanCompleteTasks = permissions.can_complete_tasks;
  const [optionsClose, setOptionsClose] = React.useState(false);

  const [markAllComplete, { loading }] = useMutation<
    ChangeTaskGroupStatusMutation,
    ChangeTaskGroupStatusMutationVariables
  >(EDIT_TASK_MUTATION, {
    refetchQueries: [
      {
        query: DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
        variables: {
          todayStart: todayStart.toString(),
          todayEnd: todayEnd.toString(),
        },
      },
    ],
    variables: { ids: tasks.filter((t) => t.task_status !== TASK_STATUS.COMPLETED).map(({ id }) => getNumericID(id)) },
  });

  React.useEffect(() => {
    setOptionsClose(false);
  });

  if (tasks.length === 0) {
    return null;
  }

  const csv = (
    <MenuItem
      key="export-to-csv"
      onClick={() => {
        const taskGroups = groupTasksWithStatus(tasks);
        const csvContent = createCSVContentFromTaskGroups([...taskGroups.values()], timezone);
        downloadCSV({
          content: csvContent,
          title: downloadTitle,
        });
        setOptionsClose(true);
      }}
      disableRipple
    >
      <DownloadIcon />
      Export to CSV
    </MenuItem>
  );

  const pdf = (
    <LoadableJSPDF key="export-to-pdf-loadable" fallback={<LoadingMenuItem />}>
      {({ jsPDF }) => (
        <MenuItem
          key="export-to-pdf-menu-item"
          onClick={() => {
            const taskGroups = groupTasksWithStatus(tasks);
            const todos = taskGroupsToStringList([...taskGroups.values()]);
            try {
              generateTodoPDF(jsPDF, {
                todos,
                downloadTitle,
                title: downloadTitle,
                timezone,
              });
            } catch (e) {
              clientLogger.error('Error generating PDF', { error: e });
              sendToServer({
                route: routes.ERROR,
                body: JSON.stringify({
                  errorMessage: 'Error generating PDF with JSPDF in task options',
                  location: window?.location?.toString() ?? 'window.location is undefined',
                }),
                title: 'no_jspdf_on_click_task_table',
              });
              enqueueSnackbar('Something went wrong generating the PDF! The team has been notified.', {
                variant: 'error',
                autoHideDuration: 10000,
              });
            }
            setOptionsClose(true);
          }}
          disableRipple
        >
          <PictureAsPdfIcon />
          Export to PDF
        </MenuItem>
      )}
    </LoadableJSPDF>
  );

  const complete = (
    <MenuItem
      key="mark-all-complete"
      onClick={() => {
        if (loading) {
          return;
        }
        void markAllComplete({
          onCompleted: () => {
            setOptionsClose(true);
            enqueueSnackbar('Completed tasks', { variant: 'success' });
            if (onSuccess) {
              onSuccess();
            }
          },
          update: (cache, { data }) => {
            const plantingsCreated = (data?.changeTaskStatuses?.plantings_created?.length ?? 0) > 0;
            const tasksCreated = (data?.changeTaskStatuses?.tasks_created?.length ?? 0) > 0;
            cache.modify({
              fields: {
                harvests(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantings(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                taskGroup(existingGroup, { DELETE }) {
                  return tasksCreated ? DELETE : existingGroup;
                },
                plantingGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                planting_groups(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
                plantingTimeGroup(existingGroup, { DELETE }) {
                  return plantingsCreated ? DELETE : existingGroup;
                },
              },
            });
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <CheckCircleIcon />
      Mark All Complete
    </MenuItem>
  );

  const allTasksCompleted = tasks.every((task) => task.task_status === TASK_STATUS.COMPLETED);
  const options = [userCanCompleteTasks && !allTasksCompleted && showComplete ? complete : undefined, csv, pdf].filter(
    utils.notNull,
  );

  return <Options useArrowOnly={useArrowOnly} useMoreIcon={false} close={optionsClose} options={options} />;
}
